// RECORDING A CHARGE — the one dialog, for every kind of charge on the money screen.
//
// THERE IS NO "ISSUE INVOICE" HERE, and the wording never implies one. The real invoice
// is produced in each partner's own accounting software; this records that it exists,
// and attaches the photo of it in the same action. That is why the file field sits in
// THIS dialog rather than behind a separate upload step: the moment you have the
// document in front of you to read its number off is the moment to file it.
//
// A PRICED CLIENT-BORNE COST NO LONGER COMES HERE FIRST. Its charge is raised in the same
// save that records the expense (server/api/rest/expenses/charge.js). What still arrives
// through this dialog is a cost whose price was never set, or one priced before charges
// were raised at entry — the pending rows, marked "טרם נקבע" when there is no price.
//
// FOUR WAYS IN, ONE FORM:
//   a retainer month   `target.retainerId` + `month` — carries the period it covers.
//   a job charge       `target.jobId` — no month; several charges on one job are fine.
//   a brokerage charge on a job that ALREADY bills — the same as above.
//   a brokerage charge on a job that is still a TOPIC — the conversion case below.
//
// THE CONVERSION IS STATED, NOT INHERITED. Recording a charge on a topic turns it into a
// billing job, and that moves money between two profit figures: the job's costs stop
// rolling up into its retainer's profit and net against its own revenue instead. The
// retainer's profit goes UP by those costs; the job starts reporting revenue − cost.
// Both are correct afterwards, but a side effect that moves two figures must be shown
// BEFORE it happens and with the real numbers — so the dialog fetches a preview and
// prints it. The old path buried this inside a dialog header nobody read.
//
// THE FIVE FIELDS THE MONEY DEPENDS ON are all here and none is optional by accident:
// the issuing partner (the standing gap credits collected cash to it — omitting it is
// the one change that would silently break the balance), the invoice's real date (which
// derives the due date from the client's terms), the period month on a retainer, the
// amount, and the labels. See server/api/rest/invoices/service.js.
//
// AND ONE QUESTION WITH NO DEFAULT: HAS THE CLIENT PAID? Usually yes by the time anyone
// records it, and then the payment is recorded in the same save — one transaction on the
// server (invoices/record.js), the same rows the two-step flow writes. "Not yet" is the
// charge alone, collected later through "רישום תשלום". See PaidNowFields.
import { useState } from 'react';
import useAsync from '../hooks/useAsync';
import { useLookups } from '../context/LookupsContext';
import { invoicesApi, expensesApi, collectionsApi, authApi } from '../api';
import he from '../i18n/he';
import Modal from './Modal';
import { Field, NumberInput, DateInput, Select, TextInput } from './Form';
import { ErrorText } from './Loader';
import Money, { Cost } from './Money';
import ConversionNotice from './ConversionNotice';
import PaidNowFields, { PAID, emptyPayment, paymentAmount } from './PaidNowFields';
import { isProject } from './engagement';

const t = he.collections.board;

// What the amount field starts at. A retainer month suggests the monthly figure; a
// brokerage charge suggests the amount the cost was marked to be charged at. Anything
// else — including a marked cost whose price was never agreed — starts blank.
const initialAmount = (card, month, expense) => {
  if (month && card.monthlyAmount != null) return String(card.monthlyAmount);

  if (expense && expense.chargeAmount != null) return String(expense.chargeAmount);
  return '';
};

export default function RecordChargeModal({
  card, month = null, expense = null, onClose, onSaved,
}) {
  const lookups = useLookups();
  const { data: partners } = useAsync(() => authApi.partners());

  // Only asked for when the charge is being raised against a marked cost on a job that
  // may still be a topic. A plain retainer month converts nothing and fetches nothing.
  const { data: preview } = useAsync(
    () => (expense ? collectionsApi.conversionPreview(expense.expenseId) : Promise.resolve(null)),
    [expense && expense.expenseId]
  );

  const [form, setForm] = useState({
    // A retainer month prefills its monthly amount, and a brokerage charge prefills what
    // the partner said they would charge when they recorded the cost (migration 054).
    // Both are PREFILLS, not facts: retainer amounts change month to month, and a charge
    // agreed weeks ago may have moved. Editable, and neither was ever money owed.
    //
    // The charge amount is often absent — the price is frequently not agreed when the
    // cost is recorded — and an absent one leaves the field EMPTY rather than falling
    // back to the cost. Prefilling the cost would propose a zero-margin charge nobody
    // agreed to, on the screen whose entire job is not to invent figures.
    amount_net: initialAmount(card, month, expense),
    issue_date: '',
    issuing_partner_id: card.issuingPartnerId || '',
    category: '',
    description: expense ? (expense.description || '') : '',
  });
  // "Has the client paid?" — unanswered until the partner answers it (see PaidNowFields).
  const [paidState, setPaidState] = useState('');
  const [payment, setPayment] = useState(emptyPayment);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // THE CONVERSION GATE. Only ever consulted when the preview says something converts,
  // so an ordinary charge is not asked to tick anything. See ConversionNotice, which
  // owns the checkbox — the gate and the warning it gates are one component on purpose.
  const [acknowledged, setAcknowledged] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const partnerOptions = (partners || []).map((p) => ({ value: p.id, label: p.name }));
  const isRetainerMonth = !!month;
  // AN EXTRA CHARGE — income beyond the monthly fee, recorded on a monthly retainer itself
  // with no month (server: invoices/extraCharge). Its description is required: with no
  // month and no fee behind it, it is all that says what was sold. A one-off project takes
  // no charge from this dialog at all; nothing opens it for one.
  const isExtra = !expense && card.kind === 'retainer' && !isProject(card) && !isRetainerMonth;
  const converts = !!(preview && preview.converts);
  const paidNow = paidState === PAID;
  const paidAmount = paymentAmount(payment, form.amount_net);

  // A conversion cannot be saved until it has been acknowledged. This dialog now opens
  // on every brokerage cost recorded against a topic, so without the gate the notice
  // above it becomes the thing everybody clicks past.
  const saveBlocked = saving
    || !(Number(form.amount_net) > 0)
    || !form.issuing_partner_id
    || (converts && !acknowledged)
    || !paidState
    || (paidNow && !(Number(paidAmount) > 0))
    || (isExtra && !form.description.trim());

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const details = {
        amount_net: Number(form.amount_net),
        issue_date: form.issue_date,
        issuing_partner_id: Number(form.issuing_partner_id) || null,
        category: form.category || null,
        description: form.description || null,
        // Already paid: the payment travels with the charge and the server writes both in
        // one transaction. A blank date means the invoice's own date.
        payment: paidNow
          ? { amount: Number(paidAmount), payment_date: payment.payment_date || null, method: payment.method }
          : null,
      };
      // A CHARGE AGAINST A COST IS ONE CALL — one transaction on the server: any topic
      // conversion, the invoice, and the link that closes the pending row. It replaced
      // four browser calls in a row, which could stop half-way and leave an invoice with
      // no cost behind it. The acknowledgement travels with it; the server refuses a
      // conversion without one.
      //
      // Anything else — a retainer month, a charge on a job with no cost behind it — is
      // the plain invoice it always was.
      const invoice = expense
        ? await expensesApi.raiseCharge(expense.expenseId, {
          ...details, convert_acknowledged: converts && acknowledged,
        })
        : await invoicesApi.create({
          ...details,
          // A retainer month, or a retainer's extra charge — which carries no month.
          retainer_id: card.kind === 'retainer' ? card.id : null,
          job_id: card.kind === 'job' ? card.id : null,
          period_month: isRetainerMonth ? month : null,
          extra: isExtra,
        });

      // The document, if one was picked. After the invoice, because the file belongs to
      // it and there is nothing to attach it to before it exists — and outside the
      // transaction, since a file store cannot roll back with a database.
      if (file) {
        const body = new FormData();
        body.append('file', file);
        await invoicesApi.uploadFile(invoice.id, body);
      }

      onSaved();
    } catch (e) {
      setError(e);
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isExtra ? he.projects.extraTitle : t.recordChargeTitle}
      onClose={onClose}
      onSave={submit}
      saveLabel={isExtra ? he.projects.extraConfirm : t.recordChargeConfirm}
      saveDisabled={saveBlocked}
    >
      <p>
        <strong>{card.title}</strong>
        <span className="muted"> · {card.clientName}</span>
        {isRetainerMonth && <span className="muted"> · {t.forMonth} {month}</span>}
      </p>

      {/* What this charge will move, with real figures, before it is saved — and the
          acknowledgement that unlocks Save. */}
      {preview && (
        <ConversionNotice
          preview={preview}
          amount={Number(form.amount_net) || 0}
          cost={expense ? expense.cost : 0}
          acknowledged={acknowledged}
          onAcknowledge={setAcknowledged}
        />
      )}

      {/* The cost this charge covers, and what was planned for it. The plan is shown so
          an edited amount can be compared against what was agreed — and named as absent
          when it is, rather than quietly omitted. */}
      {expense && (
        <p className="muted small-note">
          {t.chargingCost}: <Cost value={expense.cost} />
          {' · '}{he.expenses.chargeShort}:{' '}
          {expense.chargeAmount != null
            ? <Money value={expense.chargeAmount} />
            : he.expenses.chargeNotSet}
          {expense.description && <> · {expense.description}</>}
        </p>
      )}

      <NumberInput label={t.amount} value={form.amount_net} onChange={set('amount_net')} />
      <DateInput label={t.invoiceDate} value={form.issue_date} onChange={set('issue_date')} />
      {/* The field the standing gap depends on. Seeded from the unit's default, and
          per-charge because the two partners invoice from separate businesses and either
          may have issued this one. */}
      <Select
        label={t.issuedBy}
        value={form.issuing_partner_id}
        onChange={set('issuing_partner_id')}
        options={partnerOptions}
      />
      {/* Straight after the issuer, because when the answer is "paid" the issuer IS who
          collected it — the balance credits the payment to that partner. */}
      <PaidNowFields
        paidState={paidState}
        onPaidState={setPaidState}
        payment={payment}
        onPayment={setPayment}
        chargeAmount={form.amount_net}
      />
      {/* A label, not a figure — nothing computes from it. Blank is legitimate (a
          retainer month needs none), so the list carries an empty first option rather
          than forcing a category onto every charge. */}
      {!isRetainerMonth && (
        <Select
          label={t.category}
          value={form.category}
          onChange={set('category')}
          options={[{ value: '', label: t.noCategory }, ...lookups.incomeCategoryOptions]}
        />
      )}
      <TextInput
        label={isExtra ? he.projects.extraDescription : t.description}
        value={form.description}
        onChange={set('description')}
      />

      {/* The scan, attached in the same action that records the charge. Optional: a
          partner who has the number but not the document in hand should not be blocked
          from recording the money. */}
      <Field label={t.invoiceFile}>
        <input type="file" onChange={(e) => setFile(e.target.files[0] || null)} />
      </Field>

      {error && <ErrorText error={error} />}
    </Modal>
  );
}
